import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import {
    Box,
    Fab,
    Paper,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Avatar,
    Badge,
    Tooltip,
    Chip,
    ClickAwayListener,
} from '@mui/material';

import TourIcon from '@mui/icons-material/Tour';
import CloseIcon from '@mui/icons-material/Close';
import { tourButtonStyles } from './styles/styles';

import { Button } from 'shared';
import RefreshIcon from '@mui/icons-material/Refresh';
import PendingIcon from '@mui/icons-material/Pending';
import { useAuthService } from 'store/services/auth.service';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { selectAllTours, TOUR_METADATA, resetTour } from 'store/slices/tour.slice';
import {
    setTutorialPreference,
    skipAllTours,
    startTourManually,
    TOUR_TYPES,
} from 'store/slices/tour.slice';

const TOUR_ROUTES = {
    bank_account: '/transactions/import-bank-transactions',
    bank_upload: '/transactions/upload-transactions',
    inventory_upload: '/purchases/file-upload',
    inventory_review: '/purchases/for-review',
    email_forwarding: '/profile',
    bank_dropdown: '/transactions/import-bank-transactions',
    skybox_integration: '/profile/point-of-sale',
};

const TourButton = () => {
    const classes = tourButtonStyles();

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const tourState = useSelector(selectAllTours);

    const [open, setOpen] = useState(false);
    const [manuallyClosed, setManuallyClosed] = useState(false);
    const firstInProgressRef = useRef(null);
    const listContainerRef = useRef(null);
    const { updateEnableTutorials } = useAuthService();

    const tours = Object.keys(TOUR_METADATA).map((tourId) => {
        const metadata = TOUR_METADATA[tourId];
        const status = tourState[tourId] || { started: false, completed: false, currentStep: 0 };

        return {
            id: tourId,
            name: metadata.name,
            description: metadata.description,
            totalSteps: metadata.totalSteps,
            started: status.started || false,
            completed: status.completed || false,
            currentStep: status.currentStep || 0,
            lastUpdated: status.lastUpdated,
        };
    });

    const isAnyTourInProgress = Object.values(tourState).some(
        (tour) => tour.started && !tour.completed,
    );

    useEffect(() => {
        if (isAnyTourInProgress && !manuallyClosed) {
            setOpen(true);
        }
    }, [isAnyTourInProgress, manuallyClosed]);


    useEffect(() => {
        const isAnyTourInProgress = tours.some(tour => tour.started && !tour.completed);
        if (isAnyTourInProgress && !manuallyClosed) {
            setOpen(true);
        }
    }, [tours, manuallyClosed]);


    useEffect(() => {
        if (open && listContainerRef.current) {
  
            const timer = setTimeout(() => {
                const firstInProgress = listContainerRef.current?.querySelector('[data-in-progress="true"]');
                if (firstInProgress) {
                    firstInProgress.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                    });
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [open, tours]);

    const handleStartTutorial = () => {
        dispatch(startTourManually({ tourId: TOUR_TYPES.BANK_DROPDOWN }));
    };

    const skipTutorials = async () => {
        await updateEnableTutorials({ wants_tutorial: false });
        localStorage.setItem('showOnboardingModal', 'skipped');
        dispatch(skipAllTours());
        dispatch(setTutorialPreference({ preference: 'skipped' }));
    };

    const inProgressCount = tours.filter((tour) => tour.started && !tour.completed).length;

    const handleResetTour = (tourId, event) => {
        event.stopPropagation();
        dispatch(startTourManually({ tourId, isManual: true }));

        if (tourId === TOUR_TYPES.BANK_ACCOUNT) {
            navigate(TOUR_ROUTES[tourId], {
                state: { openManualBankModal: true, restartTour: true },
            });
        } else if (tourId === TOUR_TYPES.INVENTORY_UPLOAD) {
            navigate(TOUR_ROUTES[tourId], {
                state: { defaultTab: 'fileUpload', restartTour: true },
            });
        } else if (tourId === TOUR_TYPES.SKYBOX_INTEGRATION) {
            navigate(TOUR_ROUTES[tourId], {
                state: { restartTour: true },
            });
        } else {
            navigate(TOUR_ROUTES[tourId], {
                state: { restartTour: true },
            });
        }

        setOpen(false);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const calculateProgress = (current, total) => {
        return Math.round((current / total) * 100);
    };

    const handleClose = () => {
        setOpen(false);
        setManuallyClosed(true);
    };

    // Modified to prevent closing when clicking outside
    const handleClickAway = () => {
        // Do nothing - this prevents the tour from closing when clicking outside
    };

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Box sx={classes.tourMainButton}>
                <Badge
                    badgeContent={inProgressCount}
                    color="error"
                    overlap="circular"
                    invisible={inProgressCount === 0}
                    sx={classes.badgeSetup}
                >
                    <Fab
                        color="primary"
                        size="medium"
                        onClick={() => {
                            setOpen(!open);
                            setManuallyClosed(!open ? false : manuallyClosed);
                        }}
                        sx={classes.fabSetup}
                    >
                        {open ? <KeyboardArrowDownIcon /> : <HelpOutlineIcon />}
                    </Fab>
                </Badge>

                {open && (
                    <Paper elevation={6} sx={classes.paperSetup}>
                        <Box sx={classes.boxPaperSetup}>
                            <Typography variant="h6" sx={classes.title}>
                                Tour History
                            </Typography>
                            <IconButton size="small" onClick={(e) => {
                                e.stopPropagation();
                                handleClose();
                            }} sx={{ color: 'white' }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        <Divider />

                        <Box sx={{ maxHeight: 320, overflowY: 'auto' }} ref={listContainerRef}>
                            <List>
                                {tours.map((tour) => (
                                    <ListItem
                                        key={tour.id}
                                        data-in-progress={tour.started && !tour.completed}
                                        sx={{
                                            cursor: 'pointer',
                                            backgroundColor: (theme) =>
                                                tour.started && !tour.completed
                                                    ? theme.palette.mode === 'dark'
                                                        ? 'rgba(25, 118, 210, 0.15)'
                                                        : 'rgba(25, 118, 210, 0.08)'
                                                    : theme.palette.mode === 'dark'
                                                        ? '#121826'
                                                        : 'transparent',
                                            border: (theme) =>
                                                tour.started && !tour.completed
                                                    ? '2px solid #1976d2'
                                                    : `1px solid ${
                                                        theme.palette.mode === 'dark' ? '#0F1420' : 'transparent'
                                                    }`,
                                            borderRadius: '4px',
                                            
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: (theme) =>
                                                    theme.palette.mode === 'dark'
                                                        ? '#1B2332'
                                                        : 'rgba(0, 0, 0, 0.04)',
                                            },

                                        }}
                                        onClick={() => {
                                            if (tour.id === TOUR_TYPES.BANK_ACCOUNT) {
                                                navigate(TOUR_ROUTES[tour.id], {
                                                    state: { openManualBankModal: true },
                                                });
                                            } else if (tour.id === TOUR_TYPES.INVENTORY_UPLOAD) {
                                                navigate(TOUR_ROUTES[tour.id], {
                                                    state: { defaultTab: 'fileUpload' },
                                                });
                                            } else {
                                                navigate(TOUR_ROUTES[tour.id]);
                                            }

                                            dispatch(startTourManually({ tourId: tour.id }));

                                            // dispatch(resetTour({ tourId: tour.id }));
                                        }}
                                    >
                                        <ListItemIcon>
                                            <Avatar
                                                sx={{
                                                    bgcolor: tour.completed
                                                        ? 'success.light'
                                                        : tour.started
                                                        ? 'warning.light'
                                                        : 'grey.400',
                                                    width: 36,
                                                    height: 36,
                                                }}
                                            >
                                                {tour.completed ? (
                                                    <CheckCircleOutlineIcon />
                                                ) : tour.started ? (
                                                    <PendingIcon />
                                                ) : (
                                                    <TourIcon />
                                                )}
                                            </Avatar>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Box
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                >
                                                    <Typography variant="body1">
                                                        {tour.name}
                                                    </Typography>
                                                    {tour.started && (
                                                        <Tooltip title="Reset tour">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) =>
                                                                    handleResetTour(tour.id, e)
                                                                }
                                                            >
                                                                <RefreshIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                <Box>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        gutterBottom
                                                    >
                                                        {tour.description}
                                                    </Typography>

                                                    {tour.started && !tour.completed && (
                                                        <Box mt={0.5}>
                                                            <Typography
                                                                variant="caption"
                                                                display="block"
                                                            >
                                                                Progress: Step{' '}
                                                                {tour.currentStep + 1} of{' '}
                                                                {tour.totalSteps}(
                                                                {calculateProgress(
                                                                    tour.currentStep + 1,
                                                                    tour.totalSteps,
                                                                )}
                                                                %)
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {tour.started && (
                                                        <Box mt={0.5}>
                                                            <Chip
                                                                label={
                                                                    tour.completed
                                                                        ? 'Completed'
                                                                        : 'In Progress'
                                                                }
                                                                size="small"
                                                                color={
                                                                    tour.completed
                                                                        ? 'success'
                                                                        : 'warning'
                                                                }
                                                                sx={classes.chipSetup}
                                                            />
                                                            {tour.lastUpdated && (
                                                                <Typography
                                                                    variant="caption"
                                                                    display="block"
                                                                    mt={0.5}
                                                                >
                                                                    Last activity:{' '}
                                                                    {formatDate(tour.lastUpdated)}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>

                        {/* Footer */}
                        <Box sx={classes.footer}>
                            <Typography variant="body2" color="text.secondary">
                                {tours.filter((t) => t.completed).length} of {tours.length} tours
                                completed
                            </Typography>
                        </Box>
                        <Divider />
                        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? '#121826' : theme.backgroundColor.primary, }}>
                            <Button
                                disabled={inProgressCount === 0}
                                text="Skip Tutorials"
                                onClick={skipTutorials}
                            />
                        </Box>
                    </Paper>
                )}
            </Box>
        </ClickAwayListener>
    );
};

export default TourButton;